/**
 * Route Loader Utilities
 *
 * IMPORTANT: Always use these loader-based redirects instead of the <Navigate> component.
 *
 * Why?
 * - The <Navigate> component doesn't properly trigger `navigation.state = 'loading'`
 * - This causes page tracking to fire on intermediate routes during redirect chains
 * - Loaders run BEFORE component rendering, ensuring proper navigation state
 *
 * Pattern:
 * ```tsx
 * // ❌ DON'T use Navigate component for redirects
 * { index: true, element: <Navigate to="/path" replace /> }
 *
 * // ✅ DO use loader-based redirects
 * { index: true, loader: redirectTo('/path') }
 *
 * // ✅ For conditional redirects
 * { path: '/overview', loader: createAsyncLoader(...), element: <Overview /> }
 * ```
 */

import { type LoaderFunctionArgs, redirect } from 'react-router-dom';
import { CONTEST_REGISTRATION_PARAM } from './contest-registration.constants';

export { CONTEST_REGISTRATION_PARAM } from './contest-registration.constants';

const INITIAL_URL_KEY = 'initialUrl';

/** localStorage key for contest signup flow – skip welcome redirect when user has no account */
export const CONTEST_SIGNUP_STORAGE_KEY = 'contestSignup';

/** Set flag so overview loader redirects to Contests instead of Welcome when user has no account */
export const setContestSignupFlag = () => {
  localStorage.setItem(CONTEST_SIGNUP_STORAGE_KEY, 'true');
};

/** Returns true if flag was set and removes it (one-shot). Use in overview loader to choose redirect. */
export const getAndClearContestSignupFlag = (): boolean => {
  const value = localStorage.getItem(CONTEST_SIGNUP_STORAGE_KEY);
  if (value != null) {
    localStorage.removeItem(CONTEST_SIGNUP_STORAGE_KEY);
    return true;
  }
  return false;
};

/** Returns true if user came from contest signup page (read-only, does not clear). Use when building register payload. */
export const getContestSignupFlag = (): boolean => {
  return localStorage.getItem(CONTEST_SIGNUP_STORAGE_KEY) != null;
};

/**
 * Creates the root index loader for the private app. Redirects to contest page
 * only when the user visited with ?contest-registration=true; otherwise uses
 * stored initial URL or fallback path.
 *
 * @param fallbackPath - Path when no initial URL is stored (e.g. Overview)
 * @param contestRegistrationPath - Path when URL has contest-registration=true (e.g. Contests)
 */

export const createInitialRedirectLoader =
  (fallbackPath: string, contestRegistrationPath: string) =>
  ({ request }: LoaderFunctionArgs) => {
    const url = new URL(request.url);
    if (url.searchParams.get(CONTEST_REGISTRATION_PARAM) === 'true') {
      return redirect(contestRegistrationPath);
    }
    const initialUrl = localStorage.getItem(INITIAL_URL_KEY);
    if (initialUrl) {
      localStorage.removeItem(INITIAL_URL_KEY);
      return redirect(initialUrl);
    }
    return redirect(fallbackPath);
  };

/**
 * Creates a catch-all loader for the public (unauthenticated) router.
 * Always redirects to the specified path (typically login page).
 * If the user was trying to access a private route, saves the URL
 * for post-authentication redirect.
 *
 * @param isPrivateRoute - Function to check if a path requires authentication
 * @param isAuthenticated - Function to check if user is authenticated
 * @param redirectPath - Path to redirect to (typically login page)
 */
export const createPublicCatchAllLoader =
  (
    isPrivateRoute: (path: string) => boolean,
    isAuthenticated: () => boolean,
    redirectPath: string,
  ) =>
  ({ request }: LoaderFunctionArgs) => {
    const { pathname, search } = new URL(request.url);

    if (isPrivateRoute(pathname) && !isAuthenticated()) {
      localStorage.setItem(INITIAL_URL_KEY, pathname + search);
    }

    return redirect(redirectPath);
  };

/**
 * Simple redirect loader - cleaner alternative to inline arrow functions.
 * Use this instead of `loader: () => redirect('/path')` for better readability.
 *
 * @param path - Path to redirect to
 */
export const redirectTo = (path: string) => () => redirect(path);

/**
 * Async conditional redirect loader.
 * Redirects only if the async condition returns true.
 *
 * @param condition - Async function that returns true to trigger redirect
 * @param to - Path to redirect to when condition is true
 */
export const asyncRedirectIf =
  (condition: () => Promise<boolean>, to: string) => async () => {
    return (await condition()) ? redirect(to) : null;
  };

/**
 * Creates an async loader with error handling for conditional redirects.
 * Use this for complex redirect logic that requires async data fetching.
 *
 * @param checkCondition - Async function that returns true when redirect is needed
 * @param redirectPath - Path to redirect to when condition returns true
 * @param onError - Optional error handler (defaults to console.error)
 *
 * @example
 * const overviewLoader = createAsyncLoader(
 *   async () => {
 *     const { total } = await accountStore.paginate({ page: 1, limit: 10 });
 *     return accountStore.isDefaultFilters && total === 0;
 *   },
 *   PagePath.Welcome,
 *   (e) => console.error('Overview loader failed', e)
 * );
 */
export const createAsyncLoader =
  (
    checkCondition: () => Promise<boolean>,
    redirectPath: string,
    onError?: (error: unknown) => void,
  ) =>
  async () => {
    try {
      const shouldRedirect = await checkCondition();
      if (shouldRedirect) {
        return redirect(redirectPath);
      }
    } catch (e) {
      (onError ?? console.error)(e);
    }
    return null;
  };
